import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { DataTable, Disclosure, StatusBadge, Tooltip } from '@/components/UI';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import { describeSessionTimingPolicy, formatEasternUnix, formatWallClock, type IdeaLabRunDetail } from '../ideaLabUtils';

type CoverageSession = IdeaLabRunDetail['entryCoverage']['sessions'][number];

type ExcludedWorkUnit = IdeaLabRunDetail['excludedWorkUnits'][number];
type RunVariant = IdeaLabRunDetail['variants'][number];

const WORK_UNIT_PHASE_LABELS: Record<IdeaLabRunDetail['workUnits'][number]['phase'], string> = {
    evaluate: 'Evaluate',
    reduce: 'Reduce'
};

interface RunDiagnosticsProps {
    run: IdeaLabRunDetail;
    /** Display name of the run's option buying strategy, when the run measured options. */
    optionStrategyName: string | null;
}

const Subsection = ({ title, children }: { title: string; children: ReactNode }) => (
    <div>
        <h5 className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">{title}</h5>
        {children}
    </div>
);

const Facts = ({ children }: { children: ReactNode }) => (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-sm">{children}</dl>
);

const Fact = ({ label, children }: { label: string; children: ReactNode }) => (
    <>
        <dt className="text-zinc-500">{label}</dt>
        <dd className="break-words text-zinc-300">{children}</dd>
    </>
);

/**
 * Everything about how a run executed rather than what it found: option policy details, symbols
 * excluded for missing history, work units, compiled artifacts, and frozen settings. Collapsed by
 * default so the report stays at the top of the page.
 */
const RunDiagnostics = ({ run, optionStrategyName }: RunDiagnosticsProps) => {
    const symbolByEntity = new Map(run.entities.map((entity) => [`${entity.entityType}:${entity.entityId}`, entity.symbol]));
    const variantKeyById = new Map(run.variants.map((variant) => [variant.variantId, variant.variantKey]));
    const workUnitRows = [...run.workUnits].sort((left, right) =>
        left.phase === right.phase ? left.status.localeCompare(right.status) : left.phase === 'evaluate' ? -1 : 1
    );

    const coverageColumns: ColumnDef<CoverageSession>[] = [
        { accessorKey: 'symbol', header: 'Symbol' },
        { accessorKey: 'sessionDate', header: 'Session (Eastern)' },
        {
            id: 'reason',
            header: 'Reason',
            cell: ({ row }) =>
                row.original.reason === 'invalid_price'
                    ? `Suspect or confirmed price issue #${row.original.issueId} (revision ${row.original.issueRevision})`
                    : 'Required bars unavailable'
        }
    ];

    const excludedColumns: ColumnDef<ExcludedWorkUnit>[] = [
        {
            id: 'entity',
            header: 'Symbol',
            accessorFn: (unit) => symbolByEntity.get(`${unit.entityType}:${unit.entityId}`) ?? `#${unit.entityId}`,
            cell: ({ row }) => (
                <span className="whitespace-nowrap">
                    <span className="text-zinc-100">
                        {symbolByEntity.get(`${row.original.entityType}:${row.original.entityId}`) ?? `#${row.original.entityId}`}
                    </span>
                    <span className="ml-1 text-xs text-zinc-500">{row.original.entityType}</span>
                </span>
            )
        },
        { id: 'reason', header: 'Reason', cell: () => <span className="whitespace-nowrap">Historical data coverage</span> },
        {
            accessorKey: 'errorMessage',
            header: 'Evidence',
            cell: ({ row }) =>
                row.original.errorMessage === null ? (
                    '—'
                ) : (
                    <Tooltip content={row.original.errorMessage}>
                        <span className="line-clamp-2 max-w-3xl break-all text-xs text-amber-200/90">{row.original.errorMessage}</span>
                    </Tooltip>
                )
        },
        { accessorKey: 'id', header: 'Unit' }
    ];

    const variantColumns: ColumnDef<RunVariant>[] = [
        {
            accessorKey: 'label',
            header: 'Variant',
            cell: ({ row }) => (
                <div>
                    <Link className="font-medium text-blue-300 hover:text-blue-200" to={`/user/ideaLab/variant/${row.original.variantId}`}>
                        {row.original.label}
                    </Link>
                    <div className="text-xs text-zinc-500">
                        {row.original.variantKey} · #{row.original.variantId}
                    </div>
                </div>
            )
        },
        {
            accessorKey: 'role',
            header: 'Role',
            cell: ({ row }) => <StatusBadge status={row.original.role} tone={row.original.role === 'control' ? 'accent' : 'neutral'} />
        },
        {
            accessorKey: 'parentVariantId',
            header: 'Parent',
            cell: ({ row }) =>
                row.original.parentVariantId === null
                    ? '—'
                    : (variantKeyById.get(row.original.parentVariantId) ?? `#${row.original.parentVariantId}`)
        },
        { accessorKey: 'artifactId', header: 'Compiled artifact' }
    ];

    const optionLens = run.optionLens;
    const sections = [
        optionLens ? 'option policy' : null,
        run.entryCoverage.skippedSessionCount > 0 ? `${run.entryCoverage.skippedSessionCount} skipped symbol-sessions` : null,
        run.excludedWorkUnits.length > 0 ? `${run.excludedWorkUnits.length} excluded symbols` : null,
        'work units',
        'compiled variants',
        'frozen settings'
    ].filter((section): section is string => section !== null);

    return (
        <Disclosure
            badge={<span className="text-xs font-normal text-zinc-500">{sections.join(' · ')}</span>}
            title="Diagnostics"
            variant="card"
        >
            <div className="space-y-6">
                {optionLens && (
                    <Subsection title="Option policy">
                        <p className="mb-2 text-xs text-zinc-500">
                            Contracts are selected through the strategy-world option finder with this strategy, frozen to its selection
                            archetypes when the run was queued, and replayed with platform commissions. Returns are net dollars over the
                            one-contract opening premium.
                        </p>
                        <Facts>
                            <Fact label="Option buying strategy">
                                <Link className="text-blue-300 hover:text-blue-200" to="/user/optionBuyingStrategies">
                                    {optionStrategyName ?? `Option strategy ${optionLens.optionBuyingStrategyId}`}
                                </Link>
                                <span className="ml-2 text-xs text-zinc-500">#{optionLens.optionBuyingStrategyId}</span>
                            </Fact>
                            <Fact label="Opening action and contract">
                                {optionLens.openTradeType === 'buyToOpen' ? 'buy to open' : 'sell to open'}
                                {optionLens.fixedOptionType
                                    ? ` · ${optionLens.fixedOptionType === 'c' ? 'calls' : 'puts'}`
                                    : ' · contract type not recorded'}
                            </Fact>
                            <Fact label="Selection archetypes">
                                call #{optionLens.callArchetypeId} · put #{optionLens.putArchetypeId}
                            </Fact>
                            <Fact label="Random reference">
                                {optionLens.callBaselineCovered === null && optionLens.putBaselineCovered === null
                                    ? 'not checked'
                                    : `calls ${optionLens.callBaselineCovered ? 'covered' : 'not covered'} · puts ${optionLens.putBaselineCovered ? 'covered' : 'not covered'} at queue time (final option baselines for this duration and universe)`}
                            </Fact>
                        </Facts>
                    </Subsection>
                )}

                <p className="text-xs text-zinc-500">
                    Session coverage records describe completed evaluations under the current coverage policy. Older evaluations may have no
                    records; an empty list does not establish complete historical coverage.
                </p>

                {run.entryCoverage.skippedSessionCount > 0 && (
                    <Subsection title={`Recorded skipped symbol-sessions (${run.entryCoverage.skippedSessionCount})`}>
                        <p className="mb-2 text-xs text-zinc-500">
                            Required entry evidence was missing or unreliable in these Eastern sessions across{' '}
                            {run.entryCoverage.affectedEntityCount} symbols. Every variant skips the same affected sessions; unaffected
                            history remains included. These are session coverage counts, not estimates of missed trades or missing priced
                            outcomes. Counts include completed evaluations.
                        </p>
                        {run.entryCoverage.truncated && (
                            <p className="mb-2 text-xs text-zinc-500">
                                Showing the first {run.entryCoverage.returnedSessionCount} of {run.entryCoverage.skippedSessionCount}{' '}
                                excluded symbol-sessions.
                            </p>
                        )}
                        <DataTable columns={coverageColumns} data={run.entryCoverage.sessions} search={false} />
                    </Subsection>
                )}

                {run.excludedWorkUnits.length > 0 && (
                    <Subsection title={`Excluded symbols (${run.excludedWorkUnits.length})`}>
                        <p className="mb-2 text-xs text-zinc-500">
                            Each symbol was removed from every variant, lens, and horizon after the runtime proved required historical input
                            was unavailable. The report uses the remaining common universe. Hover the evidence for the full message.
                        </p>
                        <DataTable columns={excludedColumns} data={run.excludedWorkUnits} search={false} />
                    </Subsection>
                )}

                <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                    <Subsection title={`Compiled variants (${run.variants.length})`}>
                        <DataTable columns={variantColumns} data={run.variants} search={false} />
                    </Subsection>
                    <Subsection title="Work units by phase">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-zinc-800 text-left text-xs uppercase tracking-wide text-zinc-500">
                                    <th className="py-2 pr-4">Phase</th>
                                    <th className="py-2 pr-4">Status</th>
                                    <th className="py-2 text-right">Units</th>
                                </tr>
                            </thead>
                            <tbody>
                                {workUnitRows.map((row) => (
                                    <tr className="border-b border-zinc-800/60" key={`${row.phase}:${row.status}`}>
                                        <td className="py-1.5 pr-4 text-zinc-300">{WORK_UNIT_PHASE_LABELS[row.phase]}</td>
                                        <td className="py-1.5 pr-4 text-zinc-400">{row.status}</td>
                                        <td className="py-1.5 text-right text-zinc-200">{row.count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Subsection>
                </div>

                <Subsection title="Frozen settings">
                    <Facts>
                        <Fact label="Window (Eastern)">
                            {run.startDateTime} → {run.endDateTime}
                            {run.walkForwardStartDateTime ? ` · walk-forward from ${run.walkForwardStartDateTime}` : ''}
                        </Fact>
                        <Fact label="Knowledge cutoff">
                            {formatEasternUnix(run.knowledgeCutoffUnix)}{' '}
                            <span className="text-xs text-zinc-500">(outcomes completing later count as immature)</span>
                        </Fact>
                        <Fact label="Entry timing">
                            {run.markets.map((market) => (
                                <div key={market.entityType}>
                                    {market.entityType}: {market.afterHoursRemoved ? 'regular session' : 'all hours'} ·{' '}
                                    {describeSessionTimingPolicy(run.primaryDuration, market.sessionTimingPolicy[run.primaryDuration])}
                                </div>
                            ))}
                        </Fact>
                        <Fact label="Universe source">
                            {run.entityListId !== null ? `entity list #${run.entityListId}` : 'symbols frozen at queue time'}
                        </Fact>
                        <Fact label="Settings fingerprint">
                            <code className="text-xs">{run.settingsFingerprint}</code>
                        </Fact>
                        <Fact label="Regime release">{run.regimeReleaseId ?? 'none active'}</Fact>
                        <Fact label="Worker release">{run.workerRelease ?? 'not yet claimed'}</Fact>
                        <Fact label="Queued">{formatWallClock(run.queuedAt)}</Fact>
                        <Fact label="Started">{formatWallClock(run.startedAt)}</Fact>
                        <Fact label="Completed">{formatWallClock(run.completedAt)}</Fact>
                    </Facts>
                    <h6 className="mb-1 mt-4 text-xs uppercase tracking-wide text-zinc-500">Symbols ({run.entities.length})</h6>
                    <p className="text-sm leading-relaxed text-zinc-400">{run.entities.map((entity) => entity.symbol).join(', ')}</p>
                </Subsection>
            </div>
        </Disclosure>
    );
};

export default RunDiagnostics;
